import React, { useState, useRef, useEffect } from 'react';
import AppearanceControl from '../AppearanceControl/AppearanceControl';
import AppearanceOutcomeControl from '../AppearanceOutcomeControl/AppearanceOutcomeControl';
import CoverageControl from '../CoverageControl/CoverageControl';
import { AppearanceOutcome, AppearanceRequirement, CalendarCategory } from '../../api/CourtEventApi';
import { CourtCoverageDto, CoveringAttorneyDto } from '../../api/CoverageApi';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Stack,
  IconButton,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper
} from '@mui/material';
import { Add, Edit, Delete, List, CalendarMonth, CloudUpload } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import CalendarEditable from '../CalendarEditable/CalendarEditable';
import { CourtEventDto } from '../../api/CourtEventApi';
import UserDocumentApi from '../../api/UserDocumentApi';
import { courtEventColor } from '../../utils/courtEventColor';

// Enhanced interface to include ticket information
interface CourtEventWithTicket {
  CourtEvent_guid: string;
  CourtEvent_Start: number;
  CourtEvent_End: number;
  CourtEvent_Title: string;
  CourtEvent_Desc: string;
  CourtEvent_AppearanceRequirement?: AppearanceRequirement;
  CourtEvent_AppearanceSetBy?: string | null;
  CourtEvent_AppearanceSetAt?: string | null;
  CourtEvent_AppearanceOutcome?: AppearanceOutcome;
  CourtEvent_AppearanceOutcomeSetBy?: string | null;
  CourtEvent_AppearanceOutcomeSetAt?: string | null;
  CourtEvent_AppearanceOutcomeNote?: string | null;
  CourtEvent_IsHearing?: boolean;
  CourtEvent_CalendarCategory?: CalendarCategory;
  Ticket_guid: string;
  ticketNumber: string;
  ticketGuid: string;
}

interface ManagerTicketsListProps {
  courtEvents: CourtEventWithTicket[];
  courtEventsLoading: boolean;
  courtEventError: string | null;
  actionLoading: boolean;
  onCreateEvent: () => void;
  onEditEvent: (event: CourtEventWithTicket) => void;
  onDeleteEvent: (event: CourtEventWithTicket) => void;
  // Create button control
  disableCreateButton?: boolean;
  createButtonTooltip?: string;
  // File upload and ticket context
  currentTicket?: { Ticket_guid: string; Ticket_Number: string };
  onCreateEventWithFile?: (file: File, ticketGuid: string) => void;
  uploadingFile?: boolean;
  /** Lets the parent refresh once the appearance answer changes, so the stamp updates in place. */
  onAppearanceChanged?: (courtEventGuid: string, next: AppearanceRequirement) => void;
  /** Alert deep link: mark this setting and scroll it into view. */
  highlightedCourtEventGuid?: string | null;
  /**
   * Who is covering which setting, keyed by court-event guid.
   *
   * Passed in rather than fetched per row: a case with a dozen settings would otherwise make a
   * dozen requests to draw one chip each. Omitted entirely by callers that do not show coverage.
   */
  coverageByEvent?: Record<string, CourtCoverageDto>;
  coveringAttorneys?: CoveringAttorneyDto[];
  /** The case's court, for "who could cover this?". Null on a case with no court on file. */
  coverageCourtGuid?: string | null;
  onCoverageRecorded?: () => void;
}

const ManagerTicketsList: React.FC<ManagerTicketsListProps> = ({
  courtEvents,
  courtEventsLoading,
  courtEventError,
  actionLoading,
  onCreateEvent,
  onEditEvent,
  onDeleteEvent,
  onAppearanceChanged,
  disableCreateButton = false,
  createButtonTooltip,
  currentTicket,
  onCreateEventWithFile,
  uploadingFile = false,
  highlightedCourtEventGuid = null,
  coverageByEvent,
  coveringAttorneys,
  coverageCourtGuid = null,
  onCoverageRecorded,
}) => {
  // View mode state (list or calendar)
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  
  // Drag and drop state
  const [isDragOver, setIsDragOver] = useState(false);
  const [showNoTicketDialog, setShowNoTicketDialog] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const highlightedRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!highlightedCourtEventGuid || courtEventsLoading) return;
    highlightedRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightedCourtEventGuid, courtEventsLoading, courtEvents]);

  // Convert CourtEventWithTicket to CourtEventDto for calendar component
  const convertToCalendarEvents = (events: CourtEventWithTicket[]): CourtEventDto[] => {
    return events.map(event => ({
      CourtEvent_guid: event.CourtEvent_guid,
      CourtEvent_Start: event.CourtEvent_Start,
      CourtEvent_End: event.CourtEvent_End,
      CourtEvent_Title: event.CourtEvent_Title,
      CourtEvent_Desc: event.CourtEvent_Desc,
      Ticket_guid: event.Ticket_guid
    }));
  };

  // Handle calendar event save
  const handleCalendarSave = async (event: CourtEventDto): Promise<boolean> => {
    // Find the original event to get ticket information
    const originalEvent = courtEvents.find(e => e.CourtEvent_guid === event.CourtEvent_guid);
    if (originalEvent) {
      const eventWithTicket: CourtEventWithTicket = {
        ...event,
        ticketNumber: originalEvent.ticketNumber,
        ticketGuid: originalEvent.ticketGuid
      };
      onEditEvent(eventWithTicket);
      return true;
    }
    return false;
  };

  // Handle calendar event delete
  const handleCalendarDelete = async (event: CourtEventDto): Promise<boolean> => {
    // Find the original event to get ticket information
    const originalEvent = courtEvents.find(e => e.CourtEvent_guid === event.CourtEvent_guid);
    if (originalEvent) {
      onDeleteEvent(originalEvent);
      return true;
    }
    return false;
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!calendarRef.current?.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const file = files[0];
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Please upload an image (JPEG, PNG, GIF) or PDF file.');
      return;
    }

    // Check if we have ticket context
    if (!currentTicket || !onCreateEventWithFile) {
      setShowNoTicketDialog(true);
      return;
    }

    try {
      setUploadError(null);
      await onCreateEventWithFile(file, currentTicket.Ticket_guid);
    } catch (error) {
      setUploadError('Failed to process file. Please try again.');
      console.error('File upload error:', error);
    }
  };

  // Utility functions
  const formatEpochDate = (epochTime: number) => {
    try {
      return new Date(parseInt('' + epochTime)).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const formatEpochTime = (epochTime: number) => {
    try {
      return new Date(parseInt('' + epochTime)).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'Invalid time';
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" color="primary">
          Court Events Management
        </Typography>
        
        <Stack direction="row" spacing={2} alignItems="center">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newView) => newView && setViewMode(newView)}
            size="small"
          >
            <ToggleButton value="list">
              <List fontSize="small" sx={{ mr: 1 }} />
              List
            </ToggleButton>
            <ToggleButton value="calendar">
              <CalendarMonth fontSize="small" sx={{ mr: 1 }} />
              Calendar
            </ToggleButton>
          </ToggleButtonGroup>
          
          <Tooltip 
            title={disableCreateButton && createButtonTooltip ? createButtonTooltip : ''}
            placement="top"
          >
            <span>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={onCreateEvent}
                disabled={actionLoading || disableCreateButton}
              >
                Add Court Event
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {courtEventError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {courtEventError}
        </Alert>
      )}

      {courtEventsLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : viewMode === 'calendar' ? (
        // Calendar View with Drag and Drop
        <Box 
          ref={calendarRef}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{ position: 'relative' }}
        >
          {/* Drag and Drop Overlay */}
          {isDragOver && (
            <Paper
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1000,
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                border: '2px dashed #1976d2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 2
              }}
            >
              <CloudUpload sx={{ fontSize: 60, color: 'primary.main', opacity: 0.8 }} />
              <Typography variant="h5" color="primary" fontWeight="medium">
                {currentTicket ? 'Drop file to create court event' : 'No ticket selected'}
              </Typography>
              <Typography variant="body1" color="text.secondary" textAlign="center">
                {currentTicket 
                  ? `File will be processed with OCR for ticket ${currentTicket.Ticket_Number}`
                  : 'Open a specific ticket first to upload files for court event creation'
                }
              </Typography>
            </Paper>
          )}
          
          {uploadingFile && (
            <Paper
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1001,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 2
              }}
            >
              <CircularProgress size={60} />
              <Typography variant="h6" color="primary">
                Processing file with OCR...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we extract information from your document
              </Typography>
            </Paper>
          )}
          
          <CalendarEditable
            courtEvents={convertToCalendarEvents(courtEvents)}
            onSave={handleCalendarSave}
            onDelete={handleCalendarDelete}
          />
        </Box>
      ) : courtEvents.length > 0 ? (
        <Stack spacing={2}>
          {courtEvents.map((event) => {
            const isHighlighted = Boolean(
              highlightedCourtEventGuid && event.CourtEvent_guid === highlightedCourtEventGuid,
            );
            return (
            <Card
              key={event.CourtEvent_guid}
              ref={isHighlighted ? highlightedRef : undefined}
              sx={{
                border: '1px solid',
                borderColor: isHighlighted ? 'primary.main' : 'divider',
                borderWidth: isHighlighted ? 2 : 1,
                boxShadow: isHighlighted ? 3 : 0,
                backgroundColor: isHighlighted ? 'action.selected' : undefined,
                // Same colours as Kelsi's calendar, from the one shared function (Lloyd, Aug 18).
                // A left accent rather than a filled card: these sit on a white profile page, and
                // filling them would drown the text the calendar's white-on-solid pills carry fine.
                borderLeft: '6px solid',
                borderLeftColor: courtEventColor(event.CourtEvent_CalendarCategory),
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <CardContent>
                {/* Top row: Ticket chip on left, action buttons on right */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    component={Link}
                    to={`/ticket?Ticket_guid=${event.ticketGuid}`}
                    label={`View ticket`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    clickable
                    sx={{ 
                      textDecoration: 'none',
                      '&:hover': {
                        backgroundColor: 'primary.light'
                      }
                    }}
                  />
                  {isHighlighted && (
                    <Chip label="From alert" size="small" color="primary" />
                  )}
                  </Stack>
                  
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      size="small"
                      onClick={() => onEditEvent(event)}
                      disabled={actionLoading}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onDeleteEvent(event)}
                      disabled={actionLoading}
                    >
                      <Delete />
                    </IconButton>
                  </Stack>
                </Stack>
                
                {/* Content below */}
                <Box>
                  <Stack spacing={2}>
                      <Stack direction="row" spacing={2}>
                        <Box flex={1}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Date
                          </Typography>
                          <Typography variant="body1">
                            {formatEpochDate(event.CourtEvent_Start)}
                          </Typography>
                        </Box>
                        
                        <Box flex={1}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Start Time
                          </Typography>
                          <Typography variant="body1">
                            {formatEpochTime(event.CourtEvent_Start)}
                          </Typography>
                        </Box>
                        
                        <Box flex={1}>
                          <Typography variant="subtitle2" color="text.secondary">
                            End Time
                          </Typography>
                          <Typography variant="body1">
                            {formatEpochTime(event.CourtEvent_End)}
                          </Typography>
                        </Box>
                        
                        <Box flex={1}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Event ID
                          </Typography>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {event.CourtEvent_guid}
                          </Typography>
                        </Box>
                      </Stack>

                      {event.CourtEvent_Title && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Title
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {event.CourtEvent_Title}
                          </Typography>
                        </Box>
                      )}

                      {/* The answer Hannah reads out when a third-party company asks whether the
                          driver has to attend (Hannah Aug 8 2026 → Lloyd Aug 10). */}
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Does the client need to appear?
                        </Typography>
                        <AppearanceControl
                          courtEventGuid={event.CourtEvent_guid}
                          value={event.CourtEvent_AppearanceRequirement ?? 'NOT_REQUIRED'}
                          setBy={event.CourtEvent_AppearanceSetBy}
                          setAt={event.CourtEvent_AppearanceSetAt}
                          isHearing={event.CourtEvent_IsHearing ?? true}
                          startEpoch={event.CourtEvent_Start}
                          onChanged={(next) => onAppearanceChanged?.(event.CourtEvent_guid, next)}
                        />
                      </Box>

                      {/* Did the client actually turn up? The control renders nothing unless this
                          is a past setting the client was required to attend — the event the
                          failure-to-appear fee hangs on (Lloyd, Aug 21 2026). */}
                      <AppearanceOutcomeControl
                        courtEventGuid={event.CourtEvent_guid}
                        requirement={event.CourtEvent_AppearanceRequirement}
                        outcome={event.CourtEvent_AppearanceOutcome}
                        setBy={event.CourtEvent_AppearanceOutcomeSetBy}
                        setAt={event.CourtEvent_AppearanceOutcomeSetAt}
                        note={event.CourtEvent_AppearanceOutcomeNote}
                        startEpoch={event.CourtEvent_Start}
                        isHearing={event.CourtEvent_IsHearing ?? true}
                        onRecorded={() =>
                          onAppearanceChanged?.(
                            event.CourtEvent_guid,
                            event.CourtEvent_AppearanceRequirement ?? 'NOT_REQUIRED',
                          )
                        }
                      />

                      {/* Who stood up at this setting. Only rendered where the page loaded the
                          coverage list — the case list has no use for it. */}
                      {coveringAttorneys && (
                        <CoverageControl
                          courtEventGuid={event.CourtEvent_guid}
                          coverage={coverageByEvent?.[event.CourtEvent_guid]}
                          attorneys={coveringAttorneys}
                          courtGuid={coverageCourtGuid}
                          appearanceRequired={
                            (event.CourtEvent_AppearanceRequirement ?? 'NOT_REQUIRED') !==
                            'NOT_REQUIRED'
                          }
                          onRecorded={() => onCoverageRecorded?.()}
                        />
                      )}

                      {event.CourtEvent_Desc && (
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary">
                            Description
                          </Typography>
                          <Typography variant="body2">
                            {event.CourtEvent_Desc}
                          </Typography>
                        </Box>
                      )}
                  </Stack>
                </Box>
              </CardContent>
            </Card>
            );
          })}
        </Stack>
      ) : (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No court events scheduled
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Create court events to track hearing dates and appointments.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={onCreateEvent}
          >
            Add First Court Event
          </Button>
        </Box>
      )}

      {/* Upload Error Dialog */}
      {uploadError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {uploadError}
          <Button 
            size="small" 
            onClick={() => setUploadError(null)}
            sx={{ ml: 1 }}
          >
            Dismiss
          </Button>
        </Alert>
      )}

      {/* No Ticket Selected Dialog */}
      <Dialog 
        open={showNoTicketDialog} 
        onClose={() => setShowNoTicketDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          No Ticket Selected
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            You need to open a specific ticket first before uploading files for court event creation.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Navigate to the tickets page and select a specific ticket to upload documents and create court events.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNoTicketDialog(false)}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManagerTicketsList;